/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useContext, useState } from 'react';
import languages from '@cospired/i18n-iso-languages';
import { useTranslation } from 'react-i18next';

// UI lib components
import { Button, Group, Select } from '@mantine/core';

// UI local components
import { PageFileContext } from '@features/Resource/PageFile/context';

// API & Local types
import { SectionContentProps } from '@features/Resource/ResourceMenu/types';
import { useTranslateContentLazyQuery } from '@app/types/blackroom-api';

// Store
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';

import en from '@cospired/i18n-iso-languages/langs/en.json';

languages.registerLocale(en);

const allowedLanguageCodes = [
  'de', // allemand
  'en', // anglais
  'bg', // bulgare
  'hr', // croate
  'da', // danois
  'es', // espagnol
  'et', // estonien
  'fi', // finnois
  'fr', // français
  'el', // grec
  'hu', // hongrois
  'ga', // irlandais
  'it', // italien
  'lv', // letton
  'lt', // lituanien
  'mt', // maltais
  'nl', // néerlandais
  'pl', // polonais
  'pt', // portugais
  'ro', // roumain
  'sk', // slovaque
  'sl', // slovène
  'sv', // suédois
  'cs', // tchèque
  'zh', // chinois
  'hi', // hindi
  'ar', // arabe
  'ru', // russe
  'ja', // japonais
  'pa', // pendjabi
];

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function SectionTranslation({ resource }: SectionContentProps) {
  /* ********************************** HOOKS ********************************* */

  // State
  const [locale, setLocale] = useState<string>();

  // Localization
  const { t } = useTranslation('Translation');

  // Context
  const pageFileContext = useContext(PageFileContext);

  // Store
  const workspace = useWorkspace();

  // API
  const [translateContent, { loading: loadingTranslation }] =
    useTranslateContentLazyQuery();

  const allLanguages = languages.getNames('en');

  // Filter languages to only include the allowed ones
  const filteredLanguages = Object.fromEntries(
    Object.entries(allLanguages).filter(([code]) =>
      allowedLanguageCodes.includes(code),
    ),
  );

  /* ******************************** RENDERING ******************************* */

  return (
    <Group p="xs" gap="0.5rem" wrap="nowrap">
      <Select
        placeholder={t('Translation:Common.selectLanguage')}
        data={Object.entries(filteredLanguages).map(([locale, language]) => ({
          value: locale,
          label: language,
        }))}
        onChange={(l) => setLocale(l as string)}
        searchable
        w="220px"
      />
      <Button
        size="sm"
        radius="xs"
        onClick={async () => {
          if (locale) {
            const { data } = await translateContent({
              variables: {
                workspaceId: workspace.id,
                resourceId: resource.id,
                locale,
              },
            });
            if (data?.translateContent) {
              pageFileContext.onTranslationReady(data.translateContent);
            }
          }
        }}
        loading={loadingTranslation}
        disabled={!locale}
      >
        {t('Translation:Common.translate')}
      </Button>
    </Group>
  );
}

export default SectionTranslation;
